import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Users, Loader2, AlertTriangle, UserPlus, ShieldCheck, KeyRound, Ban, RotateCcw,
  Trash2, LogOut, Server, ShieldAlert,
} from 'lucide-react';
import { fmtDateISO } from '../lib/dateFormat';

const STATUS_STYLE = {
  active: 'bg-emerald-500/12 text-emerald-700 border-emerald-500/30',
  disabled: 'bg-destructive/12 text-destructive border-destructive/30',
};

function hasPerm(perms, mod, act) {
  if (!perms) return false;
  return perms.includes('*:*') || perms.includes(`${mod}:*`) || perms.includes(`${mod}:${act}`);
}

export default function AdminEmployees() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [perms, setPerms] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [roles, setRoles] = useState([]);
  const [denied, setDenied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editEmp, setEditEmp] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [roleModal, setRoleModal] = useState(null);
  const [confirm, setConfirm] = useState(null); // {title, desc, action}
  const [resetFor, setResetFor] = useState(null);

  const canManage = hasPerm(perms, 'employees', 'manage');
  const isSuper = perms.includes('*:*');

  const load = () => {
    setBusy(true);
    Promise.all([api.employeesList(), api.rbacCatalog()])
      .then(([e, c]) => { setEmployees(e.data.employees); setCatalog(c.data); setRoles(c.data.roles); })
      .catch((err) => { if (err?.response?.status === 403) setDenied(true); })
      .finally(() => setBusy(false));
  };
  useEffect(() => {
    document.title = 'Employees & Permissions | Admin';
    if (!loading && !user) navigate('/login');
  }, [user, loading, navigate]);
  useEffect(() => { if (user) { api.rbacMe().then(({ data }) => setPerms(data.permissions || [])).catch(() => {}); load(); } }, [user]);

  if (loading) return <Layout><div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div></Layout>;
  if (denied) return <Layout><div className="max-w-2xl mx-auto py-24 text-center"><AlertTriangle className="h-10 w-10 mx-auto mb-3 text-amber-500" /><h1 className="font-display text-2xl font-semibold">Employee management access required</h1></div></Layout>;

  const verticalModules = (catalog?.modules || []).filter((m) => m.vertical);

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-employees">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="font-display text-2xl font-semibold flex items-center gap-2"><Users className="h-6 w-6 text-accent" /> Employees &amp; permissions</h1>
            <p className="text-sm text-muted-foreground mt-1">Granular, vertical-aware access control. All changes are audit-logged.</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => navigate('/admin/providers')} variant="outline" className="rounded-xl" data-testid="link-providers"><Server className="h-4 w-4 mr-1.5" />Providers</Button>
            {canManage && <Button onClick={() => setCreateOpen(true)} className="rounded-xl bg-accent text-accent-foreground hover:bg-accent/90" data-testid="add-employee"><UserPlus className="h-4 w-4 mr-1.5" />Add employee</Button>}
          </div>
        </div>

        <Tabs defaultValue="employees">
          <TabsList className="rounded-xl">
            <TabsTrigger value="employees" data-testid="tab-employees">Employees</TabsTrigger>
            <TabsTrigger value="roles" data-testid="tab-roles">Roles</TabsTrigger>
          </TabsList>

          <TabsContent value="employees" className="mt-4">
            <Card className="rounded-2xl overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead>
                    <TableHead>Last login</TableHead><TableHead>Permissions</TableHead><TableHead>Verticals</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {employees.map((e) => (
                    <TableRow key={e.user_id} data-testid={`emp-row-${e.user_id}`}>
                      <TableCell>
                        <div className="font-medium text-sm">{e.name}</div>
                        <div className="text-xs text-muted-foreground">{e.email}</div>
                      </TableCell>
                      <TableCell><Badge variant="outline" className="rounded-full">{e.role_label}{e.is_super_admin && <ShieldCheck className="h-3 w-3 ml-1 inline text-accent" />}</Badge></TableCell>
                      <TableCell><Badge variant="outline" className={`rounded-full ${STATUS_STYLE[e.staff_status] || ''}`}>{e.staff_status}</Badge></TableCell>
                      <TableCell className="text-xs text-muted-foreground">{e.last_login ? fmtDateISO(e.last_login) : '—'}</TableCell>
                      <TableCell className="text-xs">{e.permission_mode === 'custom' ? 'Custom · ' : ''}{e.permissions_count}</TableCell>
                      <TableCell className="text-xs">{e.assigned_verticals?.length ? e.assigned_verticals.join(', ') : 'All'}</TableCell>
                      <TableCell className="text-right">
                        {canManage && (
                          <div className="flex items-center justify-end gap-1">
                            <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setEditEmp(e)} data-testid={`edit-${e.user_id}`}><KeyRound className="h-3.5 w-3.5" /></Button>
                            {e.staff_status === 'active'
                              ? <Button size="sm" variant="ghost" className="h-7 px-2 text-amber-600" onClick={() => setConfirm({ title: 'Deactivate employee?', desc: `${e.name} will lose all access immediately.`, action: () => api.employeeDeactivate(e.user_id).then(() => { toast.success('Deactivated'); load(); }).catch((x) => toast.error(x?.response?.data?.detail || 'Failed')) })} data-testid={`deactivate-${e.user_id}`}><Ban className="h-3.5 w-3.5" /></Button>
                              : <Button size="sm" variant="ghost" className="h-7 px-2 text-emerald-600" onClick={() => api.employeeActivate(e.user_id).then(() => { toast.success('Activated'); load(); })} data-testid={`activate-${e.user_id}`}><RotateCcw className="h-3.5 w-3.5" /></Button>}
                            <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setResetFor(e)} data-testid={`reset-${e.user_id}`} title="Reset password"><KeyRound className="h-3.5 w-3.5 text-sky-600" /></Button>
                            <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => api.employeeRevokeSessions(e.user_id).then(() => toast.success('Sessions revoked'))} title="Revoke sessions"><LogOut className="h-3.5 w-3.5" /></Button>
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-destructive" onClick={() => setConfirm({ title: 'Delete employee?', desc: `${e.name} will be permanently removed.`, action: () => api.employeeDelete(e.user_id).then(() => { toast.success('Deleted'); load(); }).catch((x) => toast.error(x?.response?.data?.detail || 'Failed')) })} data-testid={`delete-${e.user_id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="roles" className="mt-4">
            <div className="flex justify-end mb-3">
              {isSuper && <Button onClick={() => setRoleModal({ label: '', description: '', permissions: [] })} variant="outline" className="rounded-xl" data-testid="add-role"><UserPlus className="h-4 w-4 mr-1.5" />Custom role</Button>}
            </div>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {roles.map((r) => (
                <Card key={r.role_id} className="p-4 rounded-2xl" data-testid={`role-${r.role_id}`}>
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-sm">{r.label}</p>
                    <Badge variant="outline" className="rounded-full text-[10px]">{r.is_system ? 'System' : 'Custom'}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 min-h-[32px]">{r.description}</p>
                  <p className="text-[11px] text-muted-foreground mt-2">{r.permissions.includes('*:*') ? 'All permissions' : `${r.permissions.length} permission(s)`}</p>
                  {!r.is_system && isSuper && (
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" variant="outline" className="rounded-lg flex-1" onClick={() => setRoleModal({ ...r })}>Edit</Button>
                      <Button size="sm" variant="ghost" className="rounded-lg text-destructive" onClick={() => api.rbacDeleteRole(r.role_id).then(() => { toast.success('Role deleted'); load(); }).catch((x) => toast.error(x?.response?.data?.detail || 'Failed'))}>Delete</Button>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {createOpen && catalog && <EmployeeForm mode="create" catalog={catalog} roles={roles} verticalModules={verticalModules}
        onClose={() => setCreateOpen(false)} onSaved={() => { setCreateOpen(false); load(); }} />}
      {editEmp && catalog && <EmployeeForm mode="edit" employee={editEmp} catalog={catalog} roles={roles} verticalModules={verticalModules}
        onClose={() => setEditEmp(null)} onSaved={() => { setEditEmp(null); load(); }} />}
      {roleModal && catalog && <RoleForm role={roleModal} catalog={catalog}
        onClose={() => setRoleModal(null)} onSaved={() => { setRoleModal(null); load(); }} />}
      {resetFor && <ResetDialog employee={resetFor} onClose={() => setResetFor(null)} />}

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive"><ShieldAlert className="h-5 w-5" />{confirm?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirm?.desc}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg">Cancel</AlertDialogCancel>
            <AlertDialogAction className="rounded-lg bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => { confirm.action(); setConfirm(null); }} data-testid="confirm-action">Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}

function PermissionMatrix({ catalog, selected, onToggle }) {
  return (
    <div className="max-h-72 overflow-auto rounded-xl border border-border">
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-secondary">
          <tr>
            <th className="text-left p-2">Module</th>
            {catalog.actions.map((a) => <th key={a} className="p-1 font-medium capitalize">{a.slice(0, 3)}</th>)}
          </tr>
        </thead>
        <tbody>
          {catalog.modules.map((m) => (
            <tr key={m.key} className="border-t border-border">
              <td className="p-2 font-medium">{m.label}{m.vertical && <span className="text-[9px] text-accent ml-1">V</span>}</td>
              {catalog.actions.map((a) => {
                const perm = `${m.key}:${a}`;
                return (
                  <td key={a} className="p-1 text-center">
                    <Checkbox checked={selected.has(perm)} onCheckedChange={() => onToggle(perm)} data-testid={`perm-${perm}`} className="h-3.5 w-3.5" />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EmployeeForm({ mode, employee, catalog, roles, verticalModules, onClose, onSaved }) {
  const [name, setName] = useState(employee?.name || '');
  const [email, setEmail] = useState(employee?.email || '');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState(employee?.role || 'viewer');
  const [assigned, setAssigned] = useState(new Set(employee?.assigned_verticals || []));
  const [mode2, setMode2] = useState(employee?.permission_mode || 'role');
  const [custom, setCustom] = useState(new Set());
  const [saving, setSaving] = useState(false);

  const roleTemplate = useMemo(() => (roles.find((r) => r.role_id === role)?.permissions) || [], [role, roles]);
  useEffect(() => {
    if (mode2 === 'custom' && custom.size === 0) setCustom(new Set(roleTemplate.filter((p) => p !== '*:*')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode2]);

  const toggleCustom = (perm) => setCustom((s) => { const n = new Set(s); n.has(perm) ? n.delete(perm) : n.add(perm); return n; });
  const toggleVertical = (v) => setAssigned((s) => { const n = new Set(s); n.has(v) ? n.delete(v) : n.add(v); return n; });

  const submit = () => {
    setSaving(true);
    const base = {
      role, assigned_verticals: Array.from(assigned), permission_mode: mode2,
      custom_permissions: mode2 === 'custom' ? Array.from(custom) : [],
    };
    const p = mode === 'create'
      ? api.employeeCreate({ name, email, password, ...base })
      : api.employeeUpdate(employee.user_id, { name, ...base });
    p.then(() => { toast.success(mode === 'create' ? 'Employee created' : 'Employee updated'); onSaved(); })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Save failed'))
      .finally(() => setSaving(false));
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="employee-form">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Add employee' : `Edit ${employee.name}`}</DialogTitle>
          <DialogDescription>Assign a role, restrict verticals, and optionally fine-tune granular permissions.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-lg" data-testid="emp-name" /></div>
            <div><Label className="text-xs">Email</Label><Input value={email} disabled={mode === 'edit'} onChange={(e) => setEmail(e.target.value)} className="rounded-lg" data-testid="emp-email" /></div>
          </div>
          {mode === 'create' && <div><Label className="text-xs">Temporary password</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-lg" data-testid="emp-password" /></div>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Role</Label>
              <Select value={role} onValueChange={(v) => { setRole(v); if (mode2 === 'role') setCustom(new Set()); }}>
                <SelectTrigger className="rounded-lg" data-testid="emp-role"><SelectValue /></SelectTrigger>
                <SelectContent>{roles.map((r) => <SelectItem key={r.role_id} value={r.role_id}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Permission mode</Label>
              <Select value={mode2} onValueChange={setMode2}>
                <SelectTrigger className="rounded-lg" data-testid="emp-permmode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="role">Use role defaults</SelectItem>
                  <SelectItem value="custom">Custom permissions</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs">Restrict to verticals (none = all)</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {verticalModules.map((m) => (
                <button type="button" key={m.key} onClick={() => toggleVertical(m.key)}
                  className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${assigned.has(m.key) ? 'bg-accent text-accent-foreground border-accent' : 'border-border text-muted-foreground'}`}
                  data-testid={`vertical-${m.key}`}>{m.label}</button>
              ))}
            </div>
          </div>

          {mode2 === 'custom' && <PermissionMatrix catalog={catalog} selected={custom} onToggle={toggleCustom} />}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-lg" onClick={onClose}>Cancel</Button>
          <Button className="rounded-lg bg-accent text-accent-foreground hover:bg-accent/90" disabled={saving} onClick={submit} data-testid="emp-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RoleForm({ role, catalog, onClose, onSaved }) {
  const [label, setLabel] = useState(role.label || '');
  const [description, setDescription] = useState(role.description || '');
  const [selected, setSelected] = useState(new Set((role.permissions || []).filter((p) => p !== '*:*')));
  const [saving, setSaving] = useState(false);
  const isEdit = !!role.role_id;
  const toggle = (perm) => setSelected((s) => { const n = new Set(s); n.has(perm) ? n.delete(perm) : n.add(perm); return n; });

  const submit = () => {
    if (!label.trim()) { toast.error('Role name required'); return; }
    setSaving(true);
    const body = { label, description, permissions: Array.from(selected) };
    const p = isEdit ? api.rbacUpdateRole(role.role_id, body) : api.rbacCreateRole(body);
    p.then(() => { toast.success('Role saved'); onSaved(); })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Save failed'))
      .finally(() => setSaving(false));
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="role-form">
        <DialogHeader><DialogTitle>{isEdit ? 'Edit role' : 'Create custom role'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Name</Label><Input value={label} disabled={isEdit} onChange={(e) => setLabel(e.target.value)} className="rounded-lg" data-testid="role-name" /></div>
            <div><Label className="text-xs">Description</Label><Input value={description} onChange={(e) => setDescription(e.target.value)} className="rounded-lg" /></div>
          </div>
          <PermissionMatrix catalog={catalog} selected={selected} onToggle={toggle} />
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-lg" onClick={onClose}>Cancel</Button>
          <Button className="rounded-lg bg-accent text-accent-foreground hover:bg-accent/90" disabled={saving} onClick={submit} data-testid="role-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save role'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetDialog({ employee, onClose }) {
  const [pw, setPw] = useState('');
  const [saving, setSaving] = useState(false);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm" data-testid="reset-dialog">
        <DialogHeader><DialogTitle>Reset password</DialogTitle><DialogDescription>{employee.email} — active sessions will be revoked.</DialogDescription></DialogHeader>
        <div><Label className="text-xs">New password</Label><Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="rounded-lg" data-testid="reset-password" /></div>
        <DialogFooter>
          <Button variant="outline" className="rounded-lg" onClick={onClose}>Cancel</Button>
          <Button className="rounded-lg bg-accent text-accent-foreground hover:bg-accent/90" disabled={saving} onClick={() => { setSaving(true); api.employeeResetPassword(employee.user_id, pw).then(() => { toast.success('Password reset'); onClose(); }).catch((e) => toast.error(e?.response?.data?.detail || 'Failed')).finally(() => setSaving(false)); }} data-testid="reset-save">Reset</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
